import React from 'react'
import MatchChart from 'lib/components/charts/match-chart'
import Container from 'lib/components/charts/container'
import XAxis from 'lib/components/charts/x-axis'
import YAxis from 'lib/components/charts/y-axis'
import Grid from 'lib/components/charts/grid'
import EasingDot from './shapes/easing-dot'
import EasingDiamond from './shapes/easing-diamond'
import EasingLabel from 'lib/components/charts/shapes/easing-label'
import SmallLabelXAxis from 'lib/components/charts/small-label-x-axis'
import Trendline from 'lib/components/charts/shapes/trend-line'
import Title from './title'
import { scaleLinear } from 'd3-scale'
import { extent } from 'd3-array'
import { maintainAspectRatio } from 'lib/math'
import { getGutters } from 'lib/components/charts/layouts/gutters'

class ScatterPlot extends React.PureComponent {
  static defaultProps = {
    data: [],
    width: 300,
    height: 225,
    formatX: n => n,
    formatY: n => n,
    idealRatio: 16 / 9,
    hasLinkedDots: false,
    padding: 0.15,
    trendline: null,
    transition: 800
  }

  renderDot(x, y, dot, i) {
    if (dot.x == null || dot.y == null) {
      return null
    }

    const {
      width,
      transition,
      stiffness,
      precision,
      damping,
      hasLinkedDots
    } = this.props

    const opacity = dot.isActive ? 1 : 0.25
    const Shape = dot.isBenchmark ? EasingDiamond : EasingDot
    const factor = dot.isBenchmark ? 1.6 : 1
    let radius = Math.min(7, width * 0.01) * factor

    if (dot.isFocused && dot.isActive) {
      radius *= 1.35
    }

    let posX = x(dot.x)
    let posY = y(dot.y)
    let delay = (posX / width) * transition

    return (
      <Shape
        id={dot.id}
        key={dot.label + dot.category}
        x={posX}
        y={posY}
        isHollow={dot.isHollow}
        stiffness={stiffness}
        precision={precision}
        damping={damping}
        isLink={hasLinkedDots}
        category={dot.category}
        label={dot.label}
        delay={delay}
        r={radius}
        fill={dot.fill}
        opacity={opacity}
      />
    )
  }

  renderLabel(x, y, dot, i) {
    const { stiffness, precision, damping, width } = this.props

    const opacity = dot.isFocused ? 1 : 0

    return (
      <EasingLabel
        key={dot.label + dot.category}
        id={dot.id}
        x={x(dot.x)}
        y={y(dot.y)}
        width={width}
        stiffness={stiffness}
        precision={precision}
        damping={damping}
        opacity={opacity}
        label={dot.label}
        fill={dot.fill}
      />
    )
  }

  getXDomain() {
    const { idealRatio, data, width, height, marker } = this.props

    let currentRatio = width / height
    let values = data.map(d => d.x)

    if (marker) {
      values.push(marker.x)
    }

    return maintainAspectRatio(values, currentRatio, idealRatio)
  }

  getYDomain() {
    const { data, marker, padding } = this.props

    let values = data.map(d => d.y)

    if (marker) {
      values.push(marker.y)
    }

    let [min, max] = extent(values)

    // Increase padding to allow for the title
    let diff = (max - min) * 0.5 * padding

    return [min - diff, max + diff]
  }

  render() {
    const {
      data,
      width,
      height,
      labelX,
      labelY,
      formatX,
      formatY,
      domainX,
      domainY,
      hasLinkedDots,
      trendline,
      marker,
      median,
      title
    } = this.props

    const x = scaleLinear()
      .domain(domainX || this.getXDomain())
      .nice()

    const y = scaleLinear()
      .domain(domainY || this.getYDomain())
      .nice(5)

    let { top, bottom, left, right } = getGutters({
      width: width,
      height: height,
      left: {
        ticks: y.ticks(4),
        format: formatY,
        label: labelY.toUpperCase()
      },
      bottom: {
        ticks: x.ticks(),
        format: formatX,
        label: labelX.toUpperCase()
      }
    })

    x.range([left, width - right])
    y.range([height - bottom, top])

    return (
      <Container width={width} height={height}>
        <Grid
          scaleX={x}
          scaleY={y}
          ticksX={x.ticks()}
          ticksY={y.ticks(4)}
          equator
        />

        <Title x={left} y={top}>
          {title}
        </Title>

        {data.map((dot, i) => this.renderDot(x, y, dot, i))}

        <Trendline
          curve={trendline}
          marker={marker}
          median={median}
          points={data}
          scaleX={x}
          scaleY={y}
        />

        <MatchChart breakpoint="small" width={width}>
          <SmallLabelXAxis
            label={labelX}
            x={left}
            y={height - bottom}
            width={width - right}
          />
        </MatchChart>

        <MatchChart breakpoint="large" width={width}>
          <XAxis
            label={labelX}
            scale={x}
            height={bottom}
            format={formatX}
            ticks={x.ticks()}
            y={y.range()[0]}
          />
        </MatchChart>

        <YAxis
          label={labelY}
          scale={y}
          format={formatY}
          ticks={y.ticks(4)}
          height={height}
          width={left}
        />

        {hasLinkedDots
          ? data.map((dot, i) => this.renderLabel(x, y, dot, i))
          : null /* after dot for stacking order */}
      </Container>
    )
  }
}

export default ScatterPlot



// WEBPACK FOOTER //
// ./src/components/charts/scatterplot.js